export function rtkErrorToString (error: unknown): string {
  if (error == null) return 'unknown error'
  if (typeof error === 'string') return error
  if ((error as Record<string, unknown>).message != null && typeof (error as Record<string, unknown>).message === 'string') {
    return (error as Record<string, unknown>).message as string
  }
  if ((error as Record<string, unknown>).error != null && typeof (error as Record<string, unknown>).error === 'string') {
    return (error as Record<string, unknown>).error as string
  }
  return 'unknown error'
}

/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-explicit-any */
export function isNullOrEmpty (value: string | null | undefined): boolean {
  return value == null || value === ''
}

export function isObject (value: any): boolean {
  const type = typeof value
  return value != null && (type === 'object' || type === 'function')
}

/*
const toString = Object.prototype.toString

function getTag (value: unknown): string {
  if (value == null) {
    return value === undefined ? '[object Undefined]' : '[object Null]'
  }
  return toString.call(value)
}
*/

export function isString (value: any): boolean {
  const type = typeof value
  return type === 'string'
}

export function isBoolean (value: any): boolean {
  const type = typeof value
  return type === 'boolean'
}

export function isNumber (value: any): boolean {
  return typeof value === 'number'
}

export function isArray (value: any): boolean {
  return Array.isArray(value)
}

export class SelfRegistrationDisabledError extends Error { }

export function pick<T> (obj: T, ...paths: string[]): Partial<T> {
  if (obj == null) {
    return {} satisfies Partial<T>
  }
  const result: Partial<T> = {}

  for (const path of paths) {
    const value = obj[path as keyof T]
    if (value != null) {
      result[path as keyof T] = value
    }
  }
  return result
}

export function omit<T> (obj: T, ...paths: string[]): Partial<T> {
  if (obj == null) {
    return {} satisfies Partial<T>
  }
  const result: Partial<T> = {}

  for (const key of Object.keys(obj)) {
    if (!paths.includes(key)) {
      const value = obj[key as keyof T]
      if (value != null) {
        result[key as keyof T] = value
      }
    }
  }
  return result
}

export function bind<T extends (...args: any[]) => any> (func: T, thisArg: any, ...boundArgs: any[]): T {
  return function (this: any, ...args: any[]): any {
    return func.apply(thisArg, boundArgs.concat(args))
  } as T
}

// eslint-disable-next-line @typescript-eslint/return-await
export const sleep = async (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms))

// a function to throttle a function call
// https://codeburst.io/throttling-and-debouncing-in-javascript-b01cad5c8edf
export function throttle<T extends (...args: any[]) => void | Promise<void>> (func: T, limit: number, maxInvocations = 1): (...args: any[]) => void {
  let inThrottle: boolean
  let invocations = 0
  let returnsPromise = false
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  return function (...args: any[]): void | Promise<void> {
    if (!inThrottle || invocations < maxInvocations) {
      const result = func.apply(args)
      if (result instanceof Promise) {
        returnsPromise = true
      }
      invocations++
      if (!inThrottle) {
        inThrottle = true
        setTimeout(() => { inThrottle = false; invocations = 0 }, limit)
      }
      return result
    }
    if (returnsPromise) {
      return Promise.resolve()
    }
  } as unknown as T
}

export function debounce<T extends (...args: any[]) => void> (func: T, wait: number, options: { leading?: boolean, trailing?: boolean, maxWait?: number } = {}): T & { cancel: () => void, flush: () => void } {
  const { leading = false, trailing = true, maxWait = 0 } = options
  let boundFunc: T
  let inDebounce: boolean
  let calledSinceLeading: boolean
  let timer: any
  let maxTimer: any
  const result = function (...args: any[]): void {
    if (!inDebounce && leading) {
      func(...args)
      inDebounce = true
    } else {
      calledSinceLeading = true
      boundFunc = bind(func, null, ...args)
    }
    if (timer != null) {
      clearTimeout(timer)
      timer = undefined
    }
    timer = setTimeout(() => {
      inDebounce = false
      timer = undefined
      if (trailing && calledSinceLeading) {
        boundFunc()
      }
    }, wait)

    if (maxWait > 0 && maxTimer == null) {
      maxTimer = setTimeout(() => {
        // basically the same as flush
        if (timer != null) {
          clearTimeout(timer)
          timer = undefined
        }
        if (trailing && calledSinceLeading) {
          boundFunc()
        }
        maxTimer = undefined
      }, maxWait)
    }
  } as unknown as T & { cancel: () => void, flush: () => void }
  result.cancel = function (): void {
    if (timer != null) {
      clearTimeout(timer)
      timer = undefined
    }
    if (maxTimer != null) {
      clearTimeout(maxTimer)
      maxTimer = undefined
    }
  }
  result.flush = function (): void {
    if (timer != null) {
      clearTimeout(timer)
      timer = undefined
    }
    if (maxTimer != null) {
      clearTimeout(maxTimer)
      maxTimer = undefined
    }
    if (trailing && calledSinceLeading) {
      boundFunc()
    }
  }
  return result
}

type BreakerFn = (resetBreakerFn: () => void) => void
export function circuitBreaker<T extends (...args: any[]) => void | Promise<void>> (func: T, limit: number, breaker: number | BreakerFn, maxInvocations = 1): T {
  let inThrottle: boolean
  let inBreaker: boolean
  let returnsPromise = false
  let invocations = 0
  return function (this: any, ...args: any[]): void | Promise<void> {
    if (inBreaker) {
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const context = this
    if (!inBreaker && (!inThrottle || invocations < maxInvocations)) {
      const result = func.apply(context, args)
      if (result instanceof Promise) {
        returnsPromise = true
      }
      invocations++
      if (!inThrottle) {
        inThrottle = true
        setTimeout(() => { inThrottle = false; invocations = 0 }, limit)
      }
      return result
    } else if (!inBreaker) {
      inBreaker = true
      if (breaker != null) {
        if (typeof breaker === 'number') {
          setTimeout(() => { inThrottle = false; inBreaker = false; invocations = 0 }, breaker)
        } else {
          breaker(() => { inThrottle = false; inBreaker = false; invocations = 0 })
        }
      }
    }
    if (returnsPromise) {
      return Promise.resolve()
    }
  } as unknown as T
}

export class Duration {
  constructor (private readonly milliseconds: number) { }

  toMilliseconds (): number {
    return this.milliseconds
  }

  toSeconds (): number {
    return this.milliseconds / 1000
  }

  static Milliseconds (seconds: number): Duration {
    return new Duration(seconds * 1000)
  }

  static Seconds (seconds: number): Duration {
    return new Duration(seconds * 1000)
  }

  static Minutes (minutes: number): Duration {
    return new Duration(minutes * 60 * 1000)
  }

  static Hours (hours: number): Duration {
    return new Duration(hours * 60 * 60 * 1000)
  }

  static Days (days: number): Duration {
    return new Duration(days * 24 * 60 * 60 * 1000)
  }

  static Weeks (weeks: number): Duration {
    return new Duration(weeks * 7 * 24 * 60 * 60 * 1000)
  }

  static Months (months: number): Duration {
    return new Duration(months * 30 * 24 * 60 * 60 * 1000)
  }

  static Years (years: number): Duration {
    return new Duration(years * 365 * 24 * 60 * 60 * 1000)
  }

  static Today (hour = 0, minutes = 0, seconds = 0): Date {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minutes, seconds)
  }

  static DaysFrom (date: Date, days: number): Date {
    return new Date(date.getTime() + Duration.Days(days).toMilliseconds())
  }

  static DaysFromToday (days: number, hour = 0, minutes = 0, seconds = 0): Date {
    return Duration.DaysFrom(Duration.Today(hour, minutes, seconds), days)
  }

  static WeeksFrom (date: Date, weeks: number): Date {
    return new Date(date.getTime() + Duration.Weeks(weeks).toMilliseconds())
  }

  static WeeksFromToday (weeks: number, hour = 0, minutes = 0, seconds = 0): Date {
    return Duration.WeeksFrom(Duration.Today(hour, minutes, seconds), weeks)
  }

  static MonthsFrom (date: Date, months: number): Date {
    let year = date.getUTCFullYear()
    let month = date.getUTCMonth() + months
    if (month > 11) {
      month = 0
      year++
    }
    let day = date.getUTCDate()
    const hour = date.getUTCHours()
    const minutes = date.getUTCMinutes()
    const seconds = date.getUTCSeconds()
    const newDate = new Date(Date.UTC(year, month, day, hour, minutes, seconds))
    if (newDate.getUTCMonth() !== month) {
      // return last day of month
      day = new Date(Date.UTC(year, month + 1, 0, 0, 0, 0)).getUTCDate()
      return new Date(Date.UTC(year, month, day, hour, minutes, seconds))
    }
    return newDate
  }

  static MonthsFromToday (months: number, hour = 0, minutes = 0, seconds = 0): Date {
    return Duration.MonthsFrom(Duration.Today(hour, minutes, seconds), months)
  }

  static YearsFrom (date: Date, years: number): Date {
    const year = date.getUTCFullYear() + years
    const month = date.getUTCMonth()
    let day = date.getUTCDate()
    const hour = date.getUTCHours()
    const minutes = date.getUTCMinutes()
    const seconds = date.getUTCSeconds()

    const newDate = new Date(Date.UTC(year, month, day, hour, minutes, seconds))
    if (newDate.getUTCMonth() !== month) {
      // return last day of month
      day = new Date(Date.UTC(year, month + 1, 0, 0, 0, 0)).getUTCDate()
      return new Date(Date.UTC(year, month, day, hour, minutes, seconds))
    }
    return newDate
  }

  static YearsFromToday (years: number, hour = 0, minutes = 0, seconds = 0): Date {
    return Duration.YearsFrom(Duration.Today(hour, minutes, seconds), years)
  }
}

export function cacheParallel<T extends (...args: any[]) => Promise<unknown>> (func: T): (...args: any[]) => ReturnType<T> {
  let inParallel = false
  let resolvers: Array<(value: unknown) => void> = []
  let rejecters: Array<(reason?: any) => void> = []
  // eslint-disable-next-line @typescript-eslint/promise-function-async
  return function (...args: any[]): Promise<unknown> {
    if (!inParallel) {
      inParallel = true
      resolvers = []
      rejecters = []
      const promise = new Promise<unknown>((resolve, reject) => {
        resolvers.push(resolve)
        rejecters.push(reject)
        const result = func(...args)
        result.then((value: unknown) => {
          inParallel = false
          resolvers.forEach(resolver => { resolver(value) })
        }).catch((reason: any) => {
          inParallel = false
          rejecters.forEach(rejecter => { rejecter(reason) })
        })
      })
      return promise
    } else {
      const promise = new Promise<unknown>((resolve, reject) => {
        resolvers.push(resolve)
        rejecters.push(reject)
      })
      return promise
    }
  } as unknown as (...args: any[]) => ReturnType<T>
}

export function intervalWithImmediate<T extends (...args: any) => Promise<void> | void> (action: (...args: Parameters<T>) => ReturnType<T>, interval: number, runImmediately: boolean, ...args: Parameters<T>): { immediate: () => void, stop: () => void } {
  let running = false
  let shouldStop = false
  let intervalTimer: any | undefined

  const doAction = (): void => {
    if (running) {
      return
    }
    if (shouldStop) {
      return
    }
    if (intervalTimer != null) {
      clearTimeout(intervalTimer)
      intervalTimer = undefined
    }
    running = true
    try {
      const result = action(...args)
      if (result instanceof Promise) {
        result.then(() => {
          running = false
          if (shouldStop) {
            return
          }
          intervalTimer = setTimeout(doAction, interval)
        }).catch((err) => {
          running = false
          console.error(err)
          if (shouldStop) {
            return
          }
          intervalTimer = setTimeout(doAction, interval)
        })
      } else {
        running = false
        if (shouldStop) {
          return
        }
        intervalTimer = setTimeout(doAction, interval)
      }
    } catch (err) {
      running = false
      console.error(err)
      if (shouldStop) {
        return
      }
      intervalTimer = setTimeout(doAction, interval)
    }
  }

  const stop = (): void => {
    shouldStop = true
    if (intervalTimer != null) {
      clearInterval(intervalTimer)
    }
  }

  intervalTimer = setTimeout(doAction, runImmediately ? 0 : interval)

  return {
    immediate: doAction,
    stop
  }
}

export interface LockImplementation {
  get: () => Promise<boolean>
  release: () => Promise<void>
}

export type AsyncFunction = () => Promise<void>

export function createLock (lock: LockImplementation | number): (fn: AsyncFunction) => Promise<boolean> {
  if (typeof lock === 'number') {
    const lockExpiration = lock
    let locked = false
    let expires = 0
    lock = {
      get: async (): Promise<boolean> => {
        const now = Date.now()
        if (locked) {
          if (expires < now) {
            expires = now + lockExpiration
            return true
          }
          return false
        }
        locked = true
        expires = now + lockExpiration
        return true
      },
      release: async (): Promise<void> => {
        locked = false
      }
    }
  }

  return async (fn: AsyncFunction): Promise<boolean> => {
    // console.log('get', Date.now())
    const hasLock = await (lock as LockImplementation).get()
    if (!hasLock) {
      // console.log('lock unavailable', Date.now())
      return false
    }
    try {
      await fn()
      await (lock as LockImplementation).release()
    } catch (err) {
      // console.log('lock run complete', Date.now())
      await (lock as LockImplementation).release()
      // console.log('lock released', Date.now())
      throw err
    }
    return true
  }
}
/*
// Lodash functions below

function hasIn (obj: object, key: string): boolean {
  return obj != null && key in Object(obj)
}

// Used as references for various `Number` constants.
const INFINITY = 1 / 0
function toKey (value: any): string | symbol {
  if (typeof value === 'string' || isSymbol(value)) {
    return value
  }
  const result = `${value as string}`
  return (result === '0' && (1 / value) === -INFINITY) ? '-0' : result
}

function baseGet (obj: object, path: string | string[]): any {
  path = castPath(path, obj)

  let index = 0
  const length = path.length

  while (obj != null && index < length) {
    obj = (obj as any)[toKey(path[index++]) as string] as object
  }
  return (index > 0 && index === length) ? obj : undefined
}

function baseSet (obj: object, path: string | string[], value: any, customizer: (value: any, key: string, obj: object) => any): object {
  if (!isObject(obj)) {
    return obj
  }
  path = castPath(path, obj)

  const length = path.length
  const lastIndex = length - 1

  let index = -1
  let nested = obj

  while (nested != null && ++index < length) {
    const key = toKey(path[index])
    let newValue = value

    if (index !== lastIndex) {
      const objValue = (nested as any)[key]
      newValue = customizer != null ? customizer(objValue, key, nested) : undefined
      if (newValue === undefined) {
        newValue = isObject(objValue)
          ? objValue
          : (isIndex(path[index + 1]) ? [] : {})
      }
    }
    assignValue(nested, key, newValue)
    nested = nested[key]
  }
  return object
}

function castPath (value: unknown, obj: object): string[] {
  if (Array.isArray(value)) {
    return value
  }
  return isKey(value, obj) ? [value] : stringToPath(value)
}

function isSymbol (value: unknown): boolean {
  const type = typeof value
  return (
    type === 'symbol' ||
    (type === 'object' && value != null && getTag(value) === '[object Symbol]')
  )
}

export function isObject (value: any): boolean {
  const type = typeof value
  return value != null && (type === 'object' || type === 'function')
}

export function isString (value: any): boolean {
  const type = typeof value;
  return (
    type === 'string' ||
    (type === 'object' &&
      value != null &&
      !Array.isArray(value) &&
      getTag(value) === '[object String]')
  )
}

function isObjectLike (value: any): boolean {
  return typeof value === 'object' && value !== null;
}

// Used as references for various `Number` constants.
const MAX_SAFE_INTEGER = 9007199254740991

function isLength (value: any): boolean {
  return typeof value === 'number' && value > -1 && value % 1 === 0 && value <= MAX_SAFE_INTEGER
}

// Used to detect unsigned integer values.
const reIsUint = /^(?:0|[1-9]\d*)$/

function isIndex (value: any, length: number = MAX_SAFE_INTEGER): boolean {
  const type = typeof value

  return !(length == null || length === 0) &&
    (type === 'number' ||
      (type !== 'symbol' && reIsUint.test(value))) &&
    (value > -1 && value % 1 === 0 && value < length)
}

function isArrayLike (value: any): boolean {
  return value != null && typeof value !== 'function' && isLength(value.length)
}

const toString = Object.prototype.toString

function getTag (value: unknown): string {
  if (value == null) {
    return value === undefined ? '[object Undefined]' : '[object Null]'
  }
  return toString.call(value)
}

const charCodeOfDot = '.'.charCodeAt(0)
const reEscapeChar = /\\(\\)?/g
const rePropName = RegExp(
  // Match anything that isn't a dot or bracket.
  '[^.[\\]]+' + '|' +
  // Or match property names within brackets.
  '\\[(?:' +
  // Match a non-string expression.
  '([^"\'][^[]*)' + '|' +
  // Or match strings (supports escaping characters).
  '(["\'])((?:(?!\\2)[^\\\\]|\\\\.)*?)\\2' +
  ')\\]' + '|' +
  // Or match "" as the space between consecutive dots or empty brackets.
  '(?=(?:\\.|\\[\\])(?:\\.|\\[\\]|$))'
  , 'g')

interface HasCache { cache: Map<any, any> }

function memoize<TResult, TFunc extends (...args: string[]) => TResult>(func: TFunc, resolver?: (...args: string[]) => string): TFunc & HasCache {
  if (typeof func !== 'function' || (resolver != null && typeof resolver !== 'function')) {
    throw new TypeError('Expected a function')
  }
  const memoized = function (this: string, ...args: string[]): TResult {
    const key = resolver != null ? resolver.apply(this, args) : args[0]
    const cache = memoized.cache

    if (cache.has(key)) {
      return cache.get(key)
    }
    const result = func.apply(this, args)
    memoized.cache = cache.set(key, result) ?? cache
    return result
  }
  memoized.cache = new (memoize.Cache ?? Map)()
  return memoized as TFunc & HasCache
}

memoize.Cache = Map

const MAX_MEMOIZE_SIZE = 500

function memoizeCapped<TResult, TFunc extends (...args: string[]) => TResult> (func: TFunc): TFunc {
  const result = memoize(func, (key: string) => {
    const { cache } = result
    if (cache.size === MAX_MEMOIZE_SIZE) {
      cache.clear()
    }
    return key
  })

  return result
}

const stringToPath = memoizeCapped((str: string) => {
  const result = []
  if (str.charCodeAt(0) === charCodeOfDot) {
    result.push('')
  }
  str.replace(rePropName, (match, expression, quote, subString): string => {
    let key = match
    if (quote != null) {
      key = subString.replace(reEscapeChar, '$1')
    } else if (expression != null) {
      key = expression.trim()
    }
    result.push(key)
  })
  return result
})

const reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/
const reIsPlainProp = /^\w*$/

function isKey (value: unknown, obj: object): boolean {
  if (Array.isArray(value)) {
    return false
  }
  const type = typeof value
  if (type === 'number' || type === 'boolean' || value == null || isSymbol(value)) {
    return true
  }
  return reIsPlainProp.test(value as string) || !reIsDeepProp.test(value as string) ||
    (obj != null && value as string | number | symbol in Object(obj))
}

function basePickBy (obj: object, paths: string[], predicate: (value: any, path: string) => boolean): any {
  let index = -1
  const length = paths.length
  const result = {}

  while (++index < length) {
    const path = paths[index]
    const value = baseGet(obj, path)
    if (predicate(value, path)) {
      baseSet(result, castPath(path, obj), value)
    }
  }
  return result
}

function basePick (obj: object, ...paths: string[]): object {
  return basePickBy(obj, paths, (value, path) => hasIn(object, path))
}

export function pick (obj: object, ...paths: string[]): object {
  return obj == null ? {} : basePick(object, paths)
}
*/
